/**
 * Investments — pełna implementacja Pipeline B (2026-05-14, Etap 2).
 *
 * Lista inwestycji z GUNB pozwoleń budowlanych. Każda ma:
 *   - investment_stage (discovered → researched → contacted → spec_in → tracked → won/lost)
 *   - Decision chain (6 ról: inwestor, architekt, projektant oświetlenia, GW, EC, kontakt)
 *   - Spec-in tracking (czy nasz produkt został wpisany w spec techniczną)
 *   - Outreach log per rola
 *   - Linked tender (gdy zostanie ogłoszony przetarg)
 *
 * Cel biznesowy: wbij się w spec techniczną PRZEZ projektanta zanim
 * inwestycja stanie się formalnym przetargiem (6-18 miesięcy okno).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../components/ui/tabs';
import {
  TrendingUp, Search, MapPin, Calendar, Building2, Loader2,
  Filter, Users, Sparkles, Trophy, X as XIcon, RefreshCw, Plus,
  CheckCircle2, Clock, AlertCircle, ArrowRight, Map as MapIcon,
  LayoutGrid, List as ListIcon, GitBranch, FileSpreadsheet,
  Bookmark, BookmarkPlus, Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  listInvestments, getInvestmentStats, discoverFromGunb,
  changeInvestmentStage, exportInvestmentsXlsx,
  listFilterPresets, createFilterPreset, deleteFilterPreset,
  getMyInvScoringProfile, putMyInvScoringProfile,
} from '../lib/api';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from '../components/ui/dropdown-menu';
import PlanLock from '../components/common/PlanLock';
import KanbanBoard from '../components/investments/KanbanBoard';
import TimelineView from '../components/activity/TimelineView';


const STAGES = [
  'discovered', 'researched', 'contacted', 'spec_in', 'tracked', 'won', 'lost',
];

const STAGE_LABELS = {
  discovered:  { label: 'Wykryte', color: 'bg-zinc-500/15 text-zinc-300 border-zinc-500/30',  icon: Search },
  researched:  { label: 'Zbadane', color: 'bg-blue-500/15 text-blue-300 border-blue-500/30',  icon: Users },
  contacted:   { label: 'Kontakt', color: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30',  icon: Sparkles },
  spec_in:     { label: 'Spec-in', color: 'bg-primary/15 text-primary border-primary/40',     icon: CheckCircle2 },
  tracked:     { label: 'Tracked', color: 'bg-amber-500/15 text-amber-300 border-amber-500/30', icon: Clock },
  won:         { label: 'Wygrane', color: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40', icon: Trophy },
  lost:        { label: 'Przegrane', color: 'bg-red-500/15 text-red-300 border-red-500/30', icon: XIcon },
};

// Faza budowy (Kompas-parity 2026-05-16) — heurystyka z permit_date + skala
const CONSTRUCTION_PHASES = {
  planning:     { label: 'Planowanie',   color: 'bg-zinc-500/15 text-zinc-300 border-zinc-500/30' },
  design:       { label: 'Projektowanie 🎯', color: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40' },
  construction: { label: 'Realizacja',   color: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  fit_out:      { label: 'Wykończenie',  color: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
  completed:    { label: 'Zakończona',   color: 'bg-red-500/15 text-red-300 border-red-500/30' },
  unknown:      { label: 'Faza ?',       color: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20' },
};

function PhaseBadge({ phase, meta }) {
  const cfg = CONSTRUCTION_PHASES[phase] || CONSTRUCTION_PHASES.unknown;
  return (
    <Badge
      variant="outline"
      className={`text-[10px] ${cfg.color}`}
      title={meta?.reason ? `${meta.reason} · pewność ${Math.round((meta.confidence || 0) * 100)}%` : undefined}
    >
      🏗 {cfg.label}
    </Badge>
  );
}

const VOIVODESHIPS = [
  'mazowieckie', 'śląskie', 'wielkopolskie', 'małopolskie',
  'dolnośląskie', 'lubelskie', 'łódzkie', 'pomorskie',
  'zachodniopomorskie', 'podkarpackie', 'świętokrzyskie',
  'warmińsko-mazurskie', 'podlaskie', 'kujawsko-pomorskie',
  'lubuskie', 'opolskie',
];


function fmtPLN(v) {
  if (!v || v === 0) return '—';
  return new Intl.NumberFormat('pl-PL', { maximumFractionDigits: 0 }).format(v) + ' PLN';
}

function fmtDate(s) {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleDateString('pl-PL', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return s.slice(0, 10);
  }
}


function StageBadge({ stage }) {
  const cfg = STAGE_LABELS[stage] || STAGE_LABELS.discovered;
  const Icon = cfg.icon;
  return (
    <Badge variant="outline" className={`gap-1 ${cfg.color}`}>
      <Icon className="w-3 h-3" />
      {cfg.label}
    </Badge>
  );
}


function StatsBar({ stats }) {
  if (!stats) return null;
  return (
    <Card>
      <CardContent className="pt-4 pb-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div>
            <div className="text-xs text-muted-foreground">Łącznie</div>
            <div className="text-2xl font-bold">{stats.total}</div>
          </div>
          {STAGES.slice(0, 4).map((s) => {
            const cfg = STAGE_LABELS[s];
            const data = stats.by_stage?.[s] || { count: 0 };
            return (
              <div key={s}>
                <div className="text-xs text-muted-foreground flex items-center gap-1">
                  <cfg.icon className="w-3 h-3" />
                  {cfg.label}
                </div>
                <div className="text-2xl font-bold">{data.count}</div>
              </div>
            );
          })}
        </div>
        {stats.win_rate_pct !== null && stats.win_rate_pct !== undefined && (
          <div className="mt-3 pt-3 border-t flex items-center gap-3 text-sm">
            <Trophy className="w-4 h-4 text-emerald-400" />
            <span className="text-muted-foreground">Win rate:</span>
            <span className="font-bold text-emerald-300">{stats.win_rate_pct}%</span>
            <span className="text-xs text-muted-foreground ml-2">
              ({stats.won} wygrane, {stats.lost} przegrane)
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


function DiscoverDialog({ open, onClose, onDone, lang = 'pl' }) {
  const [running, setRunning] = useState(false);
  const [params, setParams] = useState({
    max_to_import: 20,
    min_relevance: 0.5,
    region: '',
    only_recent_days: 90,
  });
  const [result, setResult] = useState(null);

  async function run() {
    setRunning(true);
    try {
      const body = {
        ...params,
        region: params.region || null,
      };
      const r = await discoverFromGunb(body);
      setResult(r);
      toast.success(`Import: ${r.imported} nowych, ${r.skipped_existing} pominięto`);
      if (onDone) onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Błąd importu');
    } finally {
      setRunning(false);
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <Card className="max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" />
            Import z GUNB
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <label className="text-xs text-muted-foreground">Maks. do importu</label>
            <Input
              type="number"
              value={params.max_to_import}
              onChange={(e) => setParams({ ...params, max_to_import: parseInt(e.target.value) || 20 })}
              className="h-8"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Min. relevance (0.0-1.0)</label>
            <Input
              type="number"
              step="0.1"
              value={params.min_relevance}
              onChange={(e) => setParams({ ...params, min_relevance: parseFloat(e.target.value) || 0.5 })}
              className="h-8"
            />
            <p className="text-[10px] text-muted-foreground mt-0.5">
              0.5 = średnio (hotele, biurowce). 0.7 = wysoko (lighting-friendly).
            </p>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Tylko z ostatnich (dni)</label>
            <Input
              type="number"
              value={params.only_recent_days}
              onChange={(e) => setParams({ ...params, only_recent_days: parseInt(e.target.value) || 90 })}
              className="h-8"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Województwo (opcjonalnie)</label>
            <Select value={params.region || 'all'} onValueChange={(v) => setParams({ ...params, region: v === 'all' ? '' : v })}>
              <SelectTrigger className="h-8">
                <SelectValue placeholder="Wszystkie" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Wszystkie</SelectItem>
                {VOIVODESHIPS.map((v) => (
                  <SelectItem key={v} value={v}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {result && (
            <div className="text-xs space-y-1 pt-2 border-t">
              <div>✓ Zaimportowano: <strong>{result.imported}</strong></div>
              <div>⏭ Pominięto (już istniejące): {result.skipped_existing}</div>
              {result.errors > 0 && <div className="text-red-400">⚠ Błędy: {result.errors}</div>}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose}>Zamknij</Button>
            <Button onClick={run} disabled={running} className="gap-1">
              {running ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
              Importuj
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


// T67 — dialog: mój profil punktacji (per-user). Każdy handlowiec ustawia swoje wagi.
function MyScoringDialog({ open, onClose, onSaved }) {
  const [data, setData] = useState(null);
  const [prof, setProf] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      getMyInvScoringProfile().then(r => { setData(r); setProf(r.profile); }).catch(() => {});
    }
  }, [open]);
  if (!open) return null;
  const setCat = (g, v) => setProf(p => ({ ...p, category_interest: { ...(p.category_interest || {}), [g]: v } }));
  const toggleReg = (r) => setProf(p => {
    const regs = p.preferred_regions || [];
    return { ...p, preferred_regions: regs.includes(r) ? regs.filter(x => x !== r) : [...regs, r] };
  });
  async function save() {
    setSaving(true);
    try {
      await putMyInvScoringProfile({
        category_interest: prof.category_interest,
        preferred_regions: prof.preferred_regions,
        preferred_size: prof.preferred_size,
        freshness_weight: prof.freshness_weight,
      });
      toast.success('Zapisano Twój profil punktacji — ranking przeliczony');
      onSaved && onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Błąd zapisu'); }
    setSaving(false);
  }
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>🎯 Mój profil punktacji inwestycji</DialogTitle></DialogHeader>
        {!prof ? <div className="py-8 text-center text-muted-foreground text-sm">Ładowanie…</div> : (
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground">
              Ustaw co Ciebie interesuje — Twój ranking („🎯 Mój ranking") będzie inny niż kolegów.
            </p>
            <div>
              <Label className="text-xs uppercase tracking-wide">Typy obiektów (0 = pomiń, 5 = priorytet)</Label>
              <div className="space-y-2 mt-2">
                {(data.groups || []).map(g => (
                  <div key={g.key} className="flex items-center gap-3">
                    <span className="text-xs flex-1">{g.label}</span>
                    <input type="range" min="0" max="5" step="1"
                      value={(prof.category_interest || {})[g.key] ?? 3}
                      onChange={e => setCat(g.key, Number(e.target.value))}
                      className="w-40 accent-primary" />
                    <span className="text-xs w-4 text-center font-medium">{(prof.category_interest || {})[g.key] ?? 3}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide">Preferowane województwa (puste = wszystkie)</Label>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {VOIVODESHIPS.map(r => (
                  <button key={r} type="button" onClick={() => toggleReg(r)}
                    className={`px-2 py-1 rounded text-[11px] border ${
                      (prof.preferred_regions || []).includes(r)
                        ? 'border-primary/60 bg-primary/10 text-primary' : 'border-border text-muted-foreground'}`}>
                    {r}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-4 flex-wrap">
              <div>
                <Label className="text-xs">Wielkość obiektu</Label>
                <Select value={prof.preferred_size || 'any'} onValueChange={v => setProf(p => ({ ...p, preferred_size: v }))}>
                  <SelectTrigger className="h-8 mt-1 w-40"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Dowolna</SelectItem>
                    <SelectItem value="small">Małe (&lt;800 m²)</SelectItem>
                    <SelectItem value="medium">Średnie (800-3000)</SelectItem>
                    <SelectItem value="large">Duże (&gt;3000 m²)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Waga świeżości: {Math.round((prof.freshness_weight ?? 0.5) * 100)}%</Label>
                <input type="range" min="0" max="1" step="0.1"
                  value={prof.freshness_weight ?? 0.5}
                  onChange={e => setProf(p => ({ ...p, freshness_weight: Number(e.target.value) }))}
                  className="w-40 accent-primary mt-2 block" />
              </div>
            </div>
            {/* T67 F2 — co system wyuczył z Twoich działań */}
            <div className="rounded-lg border border-border p-2.5 bg-white/5">
              <div className="text-xs font-medium mb-1">🧠 Wyuczone z Twoich działań</div>
              {data.learned ? (
                <div className="text-[11px] text-muted-foreground">
                  Na podstawie <b>{data.learned.n_signals}</b> Twoich akcji (kontakty, zmiany etapu).
                  Priorytetowe typy:{' '}
                  {Object.entries(data.learned.category_interest || {})
                    .filter(([, v]) => v >= 4).map(([k]) => k).join(', ') || '—'}
                  {(data.learned.preferred_regions || []).length > 0 &&
                    <> · regiony: {data.learned.preferred_regions.join(', ')}</>}
                  <div className="mt-0.5 opacity-70">Mieszane z Twoimi ustawieniami (70/30).</div>
                </div>
              ) : (
                <div className="text-[11px] text-muted-foreground">
                  Za mało danych — system zacznie dostrajać Twój ranking automatycznie, gdy
                  zaczniesz działać na leadach (zmiana etapu, „Loguj kontakt", spec-in).
                </div>
              )}
            </div>
            <div className="flex justify-end">
              <Button onClick={save} disabled={saving} className="gap-1.5">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                Zapisz mój profil
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Investments({ lang = 'pl', tenantId }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    stage: 'all',
    region: 'all',
    industry: 'all',            // T64 — nie wymuszaj LED
    has_chain: null,
    construction_phase: 'all',
    exclude_completed: true,    // T65 — domyślnie ukryj stare/prawdopodobnie zakończone
    page: 1,
    page_size: 50,
    sort: 'permit_date',
  });
  const [search, setSearch] = useState('');
  const [discoverOpen, setDiscoverOpen] = useState(false);
  const [scoringOpen, setScoringOpen] = useState(false);  // T67 — mój profil punktacji
  const [view, setView] = useState(() => localStorage.getItem('investments_view') || 'list');

  const handleViewChange = (v) => {
    setView(v);
    try { localStorage.setItem('investments_view', v); } catch { /* */ }
  };

  const handleKanbanMove = async (itemId, fromStage, toStage) => {
    await changeInvestmentStage(itemId, toStage);
    // Optimistic state already shows new stage — just refresh in background.
    load();
    loadStats();
  };

  // Filter presets (Kompas-inspired UX, fix 2026-05-26 zgł. Izabela 0475d485)
  const [presets, setPresets] = useState([]);
  const reloadPresets = useCallback(async () => {
    try {
      const r = await listFilterPresets('investments');
      setPresets(r.items || []);
    } catch { /* silent — endpoint optional */ }
  }, []);
  useEffect(() => { reloadPresets(); }, [reloadPresets]);

  const applyPreset = (p) => {
    setFilters({ ...filters, ...(p.filters || {}), page: 1 });
    toast.success(`Wczytano: ${p.name}`);
  };

  const savePreset = async () => {
    const name = window.prompt('Nazwa presetu (np. "Mazowieckie projektowanie"):');
    if (!name || !name.trim()) return;
    try {
      const captured = {
        stage: filters.stage,
        region: filters.region,
        industry: filters.industry,
        has_chain: filters.has_chain,
        construction_phase: filters.construction_phase,
        sort: filters.sort,
      };
      await createFilterPreset(name.trim(), 'investments', captured);
      toast.success(`Zapisano: ${name}`);
      reloadPresets();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Nie udało się zapisać presetu');
    }
  };

  const removePreset = async (p) => {
    if (!window.confirm(`Usunąć preset "${p.name}"?`)) return;
    try {
      await deleteFilterPreset(p.id);
      toast.success('Usunięto');
      reloadPresets();
    } catch { toast.error('Nie udało się usunąć'); }
  };

  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    setExporting(true);
    try {
      // Respektuj aktywne filtry (te same parametry co lista)
      const params = {};
      if (filters.stage && filters.stage !== 'all') params.stage = filters.stage;
      if (filters.region && filters.region !== 'all') params.region = filters.region;
      if (filters.industry && filters.industry !== 'all') params.industry = filters.industry;
      if (filters.has_chain !== null && filters.has_chain !== 'all') params.has_chain = filters.has_chain;
      if (filters.construction_phase && filters.construction_phase !== 'all') params.construction_phase = filters.construction_phase;
      params.sort = filters.sort || 'permit_date';
      const r = await exportInvestmentsXlsx(params);
      toast.success(`Wyeksportowano: ${r.filename}`);
    } catch (err) {
      toast.error('Eksport nie powiódł się');
    } finally {
      setExporting(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...filters };
      if (params.stage === 'all') delete params.stage;
      if (params.region === 'all') delete params.region;
      if (params.industry === 'all') delete params.industry;
      if (params.has_chain === null || params.has_chain === 'all') delete params.has_chain;
      if (params.construction_phase === 'all') delete params.construction_phase;
      // 2026-05-27 fix #3: server-side search (zamiast client-side po N items)
      if (search && search.trim()) {
        params.q = search.trim();
      }
      const r = await listInvestments(params);
      setItems(r.items || []);
      setTotal(r.total || 0);
    } catch (err) {
      // T60 — widoczne logowanie błędu (żeby złapać "błąd filtra" zgłaszany przez userów)
      console.error('Investments load error:', err, 'params=', params);
      const detail = err?.response?.data?.detail || err?.message || 'nieznany błąd';
      toast.error(`Błąd ładowania inwestycji: ${detail}`);
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [filters, search]);

  const loadStats = useCallback(async () => {
    try {
      const s = await getInvestmentStats();
      setStats(s);
    } catch (err) { /* silent */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadStats(); }, [loadStats]);

  // 2026-05-27 fix #3: backend obsługuje teraz `q` param. Items są już filtrowane
  // serwerowo. Pozostawiamy filteredBySearch jako alias dla istniejących render-paths.
  const filteredBySearch = items;

  return (
    <PlanLock feature="investments_module" requiredPlan="pro" lang={lang}>
    <div className="p-6 max-w-7xl mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-primary" />
            Inwestycje (Pipeline B)
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Żywe projekty z pozwoleń budowlanych GUNB. Cel: wbij się ze swoim produktem
            przez projektanta oświetlenia ZANIM zostanie ogłoszony przetarg.
          </p>
        </div>
        <div className="flex gap-2">
          {/* View toggle (Monday-style) */}
          <div className="flex rounded-md border border-border overflow-hidden" data-testid="view-toggle">
            <Button
              variant={view === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => handleViewChange('list')}
              className="rounded-none h-8 px-2 gap-1"
              data-testid="view-list"
            >
              <ListIcon className="w-3.5 h-3.5" />
              <span className="text-xs">Lista</span>
            </Button>
            <Button
              variant={view === 'kanban' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => handleViewChange('kanban')}
              className="rounded-none h-8 px-2 gap-1 border-l border-border"
              data-testid="view-kanban"
            >
              <LayoutGrid className="w-3.5 h-3.5" />
              <span className="text-xs">Kanban</span>
            </Button>
            <Button
              variant={view === 'timeline' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => handleViewChange('timeline')}
              className="rounded-none h-8 px-2 gap-1 border-l border-border"
              data-testid="view-timeline"
            >
              <GitBranch className="w-3.5 h-3.5" />
              <span className="text-xs">Timeline</span>
            </Button>
          </div>
          {/* Filter presets — Kompas-inspired (2026-05-26) */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1" data-testid="presets-menu">
                <Bookmark className="w-3.5 h-3.5" />
                Moje presety
                {presets.length > 0 && (
                  <Badge variant="outline" className="ml-1 text-[10px] h-4 px-1">{presets.length}</Badge>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[240px]">
              <DropdownMenuLabel>Zapisane zestawy filtrów</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {presets.length === 0 ? (
                <DropdownMenuItem disabled className="text-xs text-muted-foreground italic">
                  Brak — zapisz aktualny zestaw poniżej
                </DropdownMenuItem>
              ) : (
                presets.map(p => (
                  <DropdownMenuItem
                    key={p.id}
                    className="flex items-center justify-between gap-2 group"
                    onSelect={(e) => { e.preventDefault(); applyPreset(p); }}
                  >
                    <span className="flex-1 truncate text-xs">{p.name}</span>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); removePreset(p); }}
                      className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-red-300"
                      title="Usuń"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </DropdownMenuItem>
                ))
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={(e) => { e.preventDefault(); savePreset(); }}>
                <BookmarkPlus className="w-3.5 h-3.5 mr-2" />
                <span className="text-xs">Zapisz aktualne filtry…</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Button
            variant="outline"
            onClick={handleExport}
            disabled={exporting}
            data-testid="investments-export"
            className="gap-1"
          >
            {exporting
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <FileSpreadsheet className="w-4 h-4" />}
            Eksport do Excela
          </Button>
          <Button
            onClick={() => setDiscoverOpen(true)}
            data-testid="investments-discover"
            className="gap-1"
          >
            <Sparkles className="w-4 h-4" />
            Import z GUNB
          </Button>
          <Button variant="outline" onClick={() => { load(); loadStats(); }} className="gap-1">
            <RefreshCw className="w-4 h-4" />
            Odśwież
          </Button>
        </div>
      </div>

      {/* Stats */}
      <StatsBar stats={stats} />

      {/* Filters */}
      <Card>
        <CardContent className="pt-4 pb-4 flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs text-muted-foreground">Szukaj</label>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Nazwa / inwestor / miasto..."
                className="pl-7 h-8"
              />
            </div>
          </div>
          <div className="min-w-[150px]">
            <label className="text-xs text-muted-foreground">Etap</label>
            <Select
              value={filters.stage}
              onValueChange={(v) => setFilters({ ...filters, stage: v, page: 1 })}
            >
              <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Wszystkie</SelectItem>
                {/* zgł. 1b07b99e — liczniki przy etapach: puste etapy to PRAWDA o danych
                    (nikt nie przesunął leadów), nie awaria. Pokazujemy ile jest gdzie. */}
                {STAGES.map((s) => {
                  const cnt = stats?.by_stage?.[s]?.count ?? stats?.by_stage?.[s] ?? null;
                  return (
                    <SelectItem key={s} value={s}>
                      {STAGE_LABELS[s].label}{cnt != null ? ` (${cnt})` : ''}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[90px]">
            <label className="text-xs text-muted-foreground">Na stronę</label>
            <Select
              value={String(filters.page_size || 50)}
              onValueChange={(v) => setFilters({ ...filters, page_size: Number(v), page: 1 })}
            >
              <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[10, 20, 30, 50, 100, 200].map((n) => (
                  <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[150px]">
            <label className="text-xs text-muted-foreground">Faza budowy</label>
            <Select
              value={filters.construction_phase}
              onValueChange={(v) => setFilters({ ...filters, construction_phase: v, page: 1 })}
            >
              <SelectTrigger className="h-8" data-testid="filter-construction-phase"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Wszystkie fazy</SelectItem>
                <SelectItem value="planning">🏗 Planowanie</SelectItem>
                <SelectItem value="design">🏗 Projektowanie 🎯</SelectItem>
                <SelectItem value="construction">🏗 Realizacja</SelectItem>
                <SelectItem value="fit_out">🏗 Wykończenie</SelectItem>
                <SelectItem value="completed">🏗 Zakończona</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[140px]">
            <label className="text-xs text-muted-foreground">Województwo</label>
            <Select
              value={filters.region}
              onValueChange={(v) => setFilters({ ...filters, region: v, page: 1 })}
            >
              <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Wszystkie</SelectItem>
                {VOIVODESHIPS.map((v) => (
                  <SelectItem key={v} value={v}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[140px]">
            <label className="text-xs text-muted-foreground">Sortowanie</label>
            <Select
              value={filters.sort}
              onValueChange={(v) => setFilters({ ...filters, sort: v })}
            >
              <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="permit_date">Najnowsze</SelectItem>
                <SelectItem value="value">Wartość malejąco</SelectItem>
                <SelectItem value="match">Match score</SelectItem>
                <SelectItem value="stage">Etap pipeline</SelectItem>
                <SelectItem value="my_match">🎯 Mój ranking (mój profil)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {/* T67 — edytor mojego profilu punktacji */}
          <div className="flex items-end">
            <Button variant="outline" size="sm" className="h-8" onClick={() => setScoringOpen(true)}>
              🎯 Mój profil
            </Button>
          </div>
          {/* T65 — przełącznik: ukryj prawdopodobnie zakończone (stare pozwolenia) */}
          <div className="flex items-end">
            <button
              type="button"
              onClick={() => setFilters({ ...filters, exclude_completed: !filters.exclude_completed, page: 1 })}
              className={`h-8 px-3 rounded-md text-xs border transition-colors ${
                filters.exclude_completed
                  ? 'border-primary/50 bg-primary/10 text-primary'
                  : 'border-border text-muted-foreground hover:border-primary/30'}`}
              title="Pozwolenia starsze niż 18 mc = projekt prawdopodobnie w realizacji/zakończony"
            >
              {filters.exclude_completed ? '✓ Ukryj stare (>18 mc)' : 'Pokaż też stare'}
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Timeline view (alt) */}
      {view === 'timeline' && (
        <div data-testid="investments-timeline">
          <TimelineView
            itemType="investment"
            daysBack={30}
            onItemClick={(lane) => navigate(`/investments/${encodeURIComponent(lane.item_id)}`)}
          />
        </div>
      )}

      {/* Kanban view (alt) */}
      {view === 'kanban' && (
        <div data-testid="investments-kanban">
          {loading ? (
            <div className="grid grid-cols-7 gap-2">
              {STAGES.map((s) => (
                <Skeleton key={s} className="h-80 w-full" />
              ))}
            </div>
          ) : (
            <KanbanBoard
              items={filteredBySearch}
              stages={STAGES}
              stageMeta={STAGE_LABELS}
              onMove={handleKanbanMove}
              onItemClick={(inv) => navigate(`/investments/${encodeURIComponent(inv.source_id)}`)}
            />
          )}
        </div>
      )}

      {/* List view */}
      {view === 'list' && (
      <Card data-testid="investments-list" className="glass-card">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-2">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          ) : filteredBySearch.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">
              <Building2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
              {filters.stage && filters.stage !== 'all' ? (
                <>
                  {/* zgł. 1b07b99e — pusty etap to stan danych, nie awaria filtra */}
                  <p className="text-sm">
                    Brak inwestycji w etapie „{STAGE_LABELS[filters.stage]?.label || filters.stage}".
                  </p>
                  <p className="text-xs mt-1">
                    To nie błąd — żadna inwestycja nie została jeszcze przesunięta do tego etapu.
                    Etap zmieniasz w szczegółach inwestycji lub przeciągając kartę w widoku Kanban.
                  </p>
                  <Button variant="outline" size="sm" className="mt-3"
                          onClick={() => setFilters({ ...filters, stage: 'all', page: 1 })}>
                    Pokaż wszystkie
                  </Button>
                </>
              ) : (
                <>
                  <p className="text-sm">Brak inwestycji.</p>
                  <p className="text-xs mt-1">
                    Kliknij "Import z GUNB" aby zaciągnąć pozwolenia budowlane.
                  </p>
                </>
              )}
            </div>
          ) : (
            <div className="divide-y divide-border">
              {filteredBySearch.map((inv) => {
                const chainPct = inv.chain_total_count
                  ? Math.round((inv.chain_filled_count / inv.chain_total_count) * 100)
                  : 0;
                return (
                  <div
                    key={inv.source_id}
                    className="p-3 hover:bg-muted/30 cursor-pointer transition-colors"
                    onClick={() => navigate(`/investments/${encodeURIComponent(inv.source_id)}`)}
                    data-testid={`investment-row-${inv.source_id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <StageBadge stage={inv.investment_stage || 'discovered'} />
                          <PhaseBadge
                            phase={inv.construction_phase || 'unknown'}
                            meta={inv.construction_phase_meta}
                          />
                          {/* T65 — uczciwa flaga: stare pozwolenie = możliwe że zakończone */}
                          {inv.is_stale && (
                            <Badge className="bg-red-500/15 text-red-300 border-red-500/40 text-[10px]" title={inv.stale_warning}>
                              ⚠ stare pozwolenie
                            </Badge>
                          )}
                          {!inv.is_stale && inv.is_advanced && (
                            <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30 text-[10px]" title={inv.stale_warning}>
                              zaawansowane?
                            </Badge>
                          )}
                          {inv.match_score >= 70 && (
                            <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 text-[10px]">
                              match {inv.match_score}
                            </Badge>
                          )}
                          {/* T67 — mój score (per-user) */}
                          {typeof inv.user_score === 'number' && (
                            <Badge className="bg-primary/15 text-primary border-primary/40 text-[10px]"
                                   title="Twój score wg Twojego profilu punktacji">
                              🎯 {inv.user_score}
                            </Badge>
                          )}
                          <Badge variant="outline" className="text-[10px]">
                            {inv.source === 'gunb' ? '🏛 GUNB' : '✋ Manual'}
                          </Badge>
                          {inv.specced_products?.length > 0 && (
                            <Badge className="bg-primary/15 text-primary border-primary/40 text-[10px] gap-1">
                              <CheckCircle2 className="w-2.5 h-2.5" />
                              {inv.specced_products.length} spec-in
                            </Badge>
                          )}
                          {inv.linked_tender_id && (
                            <Badge variant="outline" className="text-[10px] gap-1">
                              <ArrowRight className="w-2.5 h-2.5" />
                              przetarg
                            </Badge>
                          )}
                        </div>
                        <div className="font-medium text-sm">{inv.title}</div>
                        <div className="text-xs text-muted-foreground flex items-center gap-3 mt-1 flex-wrap">
                          {inv.investor_name && (
                            <span className="flex items-center gap-1">
                              <Building2 className="w-3 h-3" />
                              {inv.investor_name.slice(0, 50)}
                            </span>
                          )}
                          {inv.city && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {inv.city}{inv.region ? `, ${inv.region}` : ''}
                            </span>
                          )}
                          {inv.permit_date && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {fmtDate(inv.permit_date)}
                            </span>
                          )}
                          {inv.estimated_value > 0 && (
                            <span>{fmtPLN(inv.estimated_value)}</span>
                          )}
                        </div>
                        {/* Rozróżnik dla generycznych tytułów GUNB (np. „Budowa budynku usługowego" ×35) —
                            adres + nr pozwolenia jednoznacznie odróżniają osobne inwestycje. */}
                        {(inv.address || inv.gunb_permit_id) && (
                          <div className="text-[10px] text-muted-foreground/70 mt-0.5 truncate">
                            {inv.address && <span>📍 {inv.address}</span>}
                            {inv.address && inv.gunb_permit_id && <span> · </span>}
                            {inv.gunb_permit_id && <span className="font-mono">poz. {inv.gunb_permit_id}</span>}
                          </div>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="text-[10px] text-muted-foreground">Łańcuch</div>
                        <div className="flex items-center gap-1.5">
                          <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                            <div
                              className="h-full bg-primary transition-all"
                              style={{ width: `${chainPct}%` }}
                            />
                          </div>
                          <span className="text-xs font-medium">{inv.chain_filled_count}/{inv.chain_total_count}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      )}

      {/* T39 2026-05-29 — paginacja (zgł. 6d8e68f8: brak kolejnych stron) */}
      {total > (filters.page_size || 50) && (view === 'list' || view === 'kanban') && (() => {
        const pageSize = filters.page_size || 50;
        const curPage = filters.page || 1;
        const totalPages = Math.ceil(total / pageSize);
        return (
          <div className="flex items-center justify-center gap-3 py-3">
            <Button
              variant="outline" size="sm"
              disabled={curPage <= 1 || loading}
              onClick={() => { setFilters({ ...filters, page: curPage - 1 }); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            >
              ← Poprzednia
            </Button>
            <span className="text-sm text-muted-foreground">
              Strona <strong>{curPage}</strong> z {totalPages}
              <span className="text-xs ml-2">({total.toLocaleString('pl-PL')} łącznie)</span>
            </span>
            <Button
              variant="outline" size="sm"
              disabled={curPage >= totalPages || loading}
              onClick={() => { setFilters({ ...filters, page: curPage + 1 }); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            >
              Następna →
            </Button>
          </div>
        );
      })()}

      {total > 0 && total <= (filters.page_size || 50) && (view === 'list' || view === 'kanban') && (
        <div className="text-xs text-muted-foreground text-center">
          Pokazano {filteredBySearch.length} z {total}
        </div>
      )}

      <DiscoverDialog
        open={discoverOpen}
        onClose={() => setDiscoverOpen(false)}
        onDone={() => { load(); loadStats(); }}
        lang={lang}
      />
      <MyScoringDialog
        open={scoringOpen}
        onClose={() => setScoringOpen(false)}
        onSaved={() => { setFilters(f => ({ ...f, sort: 'my_match', page: 1 })); load(); }}
      />
    </div>
    </PlanLock>
  );
}
